// /src/pages/Monetize/ListMonetize.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import { Modal as MuiModal, Box, Typography, IconButton } from '@mui/material'; // MUI Modal and components
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt, faEdit, faExclamationTriangle, faTimes } from '@fortawesome/free-solid-svg-icons'; // Added faTimes for close
import Swal from 'sweetalert2';
import { useParams} from 'react-router-dom';

import backendURL from "../../config";
import MonetizeForm from "./MonetizeForm";

// Note: Modal.setAppElement("#root"); is for react-modal and can be removed if not used elsewhere.

// Custom Styles for the MUI Modal (using sx prop)
const muiModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: '80%', md: '700px' }, // Responsive width
  maxWidth: '750px',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  border: 'none', // MUI Box handles border/shadow via elevation or sx
  borderRadius: '8px',
  boxShadow: 24, // MUI elevation for shadow
  p: 3, // Padding (theme.spacing(3))
  display: 'flex',
  flexDirection: 'column',
  outline: 'none', // Remove default focus outline
};

// Custom styles for the DataTable (remains the same)
const customTableStyles = {
    headRow: { style: { backgroundColor: '#f8f9fa', borderBottomWidth: '2px', borderBottomColor: '#dee2e6', borderBottomStyle: 'solid', fontWeight: 'bold' } },
    headCells: { style: { fontSize: '14px', color: '#495057' } },
    cells: { style: { fontSize: '14px', padding: '10px 12px' } },
    pagination: { style: { borderTopStyle: 'solid', borderTopWidth: '1px', borderTopColor: '#dee2e6' } },
};

export default function ListMonetize({ refresh }) {
  const [monetizeData, setMonetizeData] = useState([]);
  const [filteredMonetizeData, setFilteredMonetizeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState({ message: null, type: null });
  const [modalMode, setModalMode] = useState(null); // 'add', 'edit', or null (closed)
  const [isModalOpen, setIsModalOpen] = useState(false); // Separate state for MUI Modal
  const [currentMonetize, setCurrentMonetize] = useState(null);
  const [triggerFetch, setTriggerFetch] = useState(false);
  const [searchText, setSearchText] = useState("");
  const { chatbotId : paramsChatbotId } = useParams();

  // --- Data Fetching ---
  const fetchMonetizeData = useCallback(async () => {
    setLoading(true);
    setError({ message: null, type: null });
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError({ message: "Authentication token not found. Please log in.", type: 'error' });
        setLoading(false);
        return;
      }
      const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${backendURL}/get-monetizes`, { headers, params });
      const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
      const data = Array.isArray(responseData) ? responseData : [];
      setMonetizeData(data);
      setFilteredMonetizeData(data);
    } catch (err) {
      console.error("Error fetching advertisement data:", err.response || err);
      const message = err.response?.data?.message || err.response?.data?.error || "Failed to fetch advertisement data.";
      setError({ message, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Advertisements | KlonIT";
    fetchMonetizeData();
  }, [refresh, triggerFetch, fetchMonetizeData]);

  // --- Filtering ---
  useEffect(() => {
    const lowercasedSearchText = searchText.toLowerCase();
    const filtered = monetizeData.filter((item) => (
      item.title?.toLowerCase().includes(lowercasedSearchText) ||
      item.page_content?.toLowerCase().includes(lowercasedSearchText) ||
      item.page_url?.toLowerCase().includes(lowercasedSearchText) ||
      item.days?.toString().includes(lowercasedSearchText) ||
      item.expired_date?.toLowerCase().includes(lowercasedSearchText) ||
      item.upload_date?.toLowerCase().includes(lowercasedSearchText)
    ));
    setFilteredMonetizeData(filtered);
  }, [searchText, monetizeData]);

  // --- Modal Handling ---
  const openModal = (mode, data = null) => {
    setModalMode(mode);
    setCurrentMonetize(data);
    setError({ message: null, type: null });
    setIsModalOpen(true); // Open MUI Modal
  };

  const closeModal = () => {
    setIsModalOpen(false); // Close MUI Modal
    // Delay resetting mode and data to allow for MUI Modal's closing animation
    setTimeout(() => {
        setModalMode(null);
        setCurrentMonetize(null);
    }, 300); // Adjust timing based on MUI transition duration if needed
  };

  const handleSaveSuccess = () => {
    setError({ message: "Advertisement saved successfully!", type: 'success' });
    setTriggerFetch(prev => !prev);
    // The form itself will call its `onClose` prop, which is `closeModal` here.
  };

  // --- Delete Handling ---
  const handleDelete = (dataId, dataTitle) => {
      Swal.fire({
          title: 'Are you sure?',
          html: `You are about to delete the advertisement: <br/><b>${dataTitle}</b><br/>This action cannot be undone.`,
          icon: 'warning', iconColor: '#d33', showCancelButton: true, confirmButtonColor: '#d33',
          cancelButtonColor: '#6c757d', confirmButtonText: 'Yes, delete it!',
          customClass: { popup: 'swal-popup', title: 'swal-title', htmlContainer: 'swal-html-container' }
      }).then(async (result) => {
          if (result.isConfirmed) {
              setLoading(true);
              try {
                  const token = localStorage.getItem("access_token");
                  if (!token) throw new Error("Authentication token not found.");
                  const headers = { Authorization: `Bearer ${token}` };
                  await axios.delete(`${backendURL}/delete-monetize/${dataId}`, { headers });
                  setError({ message: `Advertisement "${dataTitle}" deleted successfully.`, type: 'success' });
                  setTriggerFetch(prev => !prev);
              } catch (err) {
                  console.error("Error deleting data:", err.response || err);
                  const message = err.response?.data?.message || err.response?.data?.error || "Failed to delete advertisement.";
                  setError({ message, type: 'error' });
                  setLoading(false);
              }
          }
      });
  };

  // --- DataTable Columns Definition ---
  const columns = [
    { name: "Title", selector: (row) => row.title, sortable: true, wrap: true, grow: 2 , maxWidth: "80px" },
    { name: "URL", selector: (row) => row.page_url, sortable: true, wrap: true, grow: 2, cell: row => <a href={row.page_url} target="_blank" rel="noopener noreferrer">{row.page_url}</a> },
    { name: "Featured", selector: (row) => (row.is_default ? "Yes" : "No"), sortable: true, center: true, maxWidth: "200px" },
    { name: "Days", selector: (row) => row.days, sortable: true, center: true, maxWidth: "80px" },
    { name: "Expires", selector: (row) => row.expired_date ? new Date(row.expired_date).toLocaleDateString() : 'N/A', sortable: true, center: true, },
    { name: "Uploaded", selector: (row) => row.upload_date ? new Date(row.upload_date).toLocaleDateString() : 'N/A', sortable: true, center: true, },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex">
          <button className="btn btn-sm btn-outline-primary me-2" onClick={() => openModal('edit', row)} title="Edit Advertisement" aria-label="Edit Advertisement">
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row.id, row.title)} title="Delete Advertisement" aria-label="Delete Advertisement">
            <FontAwesomeIcon icon={faTrashAlt} />
          </button>
        </div>
      ),
      ignoreRowClick: true, allowOverflow: true, button: true, width: '120px'
    },
  ];

  // --- Render Logic ---
  return (
    <div className="container-fluid p-3 mt-5">
      <div className="card shadow-sm">
        <div className="card-header bg-light d-flex justify-content-between align-items-center py-3">
           <h3 className="mb-0 card-title-text text-bold">Manage Advertisements</h3>
           <div className="d-flex align-items-center">
                <div className="d-flex align-items-baseline mx-2" >
                  <button className="btn btn-primary form-control form-control-sm me-2" onClick={() => openModal('add')}>
                      <FontAwesomeIcon icon={faPlus} className="me-1" /> Add Advertisement
                  </button>
                  <input
                    type="text" placeholder="Search..." className="form-control form-control-sm me-2"
                    style={{ width: '250px' }} value={searchText} onChange={(e) => setSearchText(e.target.value)}
                    aria-label="Search Advertisements"
                  />
                </div>
           </div>
        </div>
        <div className="card-body">
          {loading && <div className="text-center my-3"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>}
          {error.message && (
             <div className={`alert ${error.type === 'error' ? 'alert-danger' : 'alert-success'} alert-dismissible fade show`} role="alert">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" /> {error.message}
                <button type="button" className="btn-close" onClick={() => setError({ message: null, type: null })} aria-label="Close"></button>
            </div>
          )}
          {!loading && (
            <DataTable columns={columns} data={filteredMonetizeData} pagination responsive highlightOnHover pointerOnHover
              noHeader customStyles={customTableStyles}
              noDataComponent={<div className="text-center p-4">No advertisement data available.</div>}
            />
          )}
        </div>
      </div>

      {/* MUI Modal for Add/Edit */}
      <MuiModal
        open={isModalOpen}
        onClose={closeModal} // Handles backdrop click and Escape key
        aria-labelledby="monetize-modal-title"
        aria-describedby="monetize-modal-description"
        closeAfterTransition // Better for animations
        // BackdropComponent={Backdrop} // Optional: for custom backdrop
        // BackdropProps={{ timeout: 500 }} // Optional: for custom backdrop
      >
        <Box sx={muiModalStyle}>
          {/* Modal Header with Title and Close Button */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, borderBottom: 1, borderColor: 'divider', pb:1 }}>
            <Typography id="monetize-modal-title" variant="h6" component="h2">
              {modalMode === 'edit' ? "Edit Advertisement" : "Add New Advertisement"}
            </Typography>
            <IconButton onClick={closeModal} aria-label="close modal" size="small">
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          </Box>

          {/* Scrollable content area for the form */}
          <Box sx={{ overflowY: 'auto', overflowX: 'hidden', flexGrow: 1 }}>
            {/* Conditionally render the form only when modalMode is set (avoids rendering with null data during close animation) */}
            {modalMode && (
              <MonetizeForm
                  mode={modalMode}
                  chatbotId={paramsChatbotId}
                  initialData={currentMonetize}
                  onClose={closeModal}
                  onSaveSuccess={handleSaveSuccess}
              />
            )}
          </Box>
        </Box>
      </MuiModal>
    </div>
  );
}